import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSettings, createSetting, updateSetting, deleteSetting } from "../slices/settings/thunk";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Container, Table, Spinner, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup, Label } from "reactstrap";
import BreadCrumb from "../Components/Common/BreadCrumb";
import { toast } from 'react-toastify';

const SettingsDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { settings, loading, error } = useSelector(state => state.setting);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [form, setForm] = useState({ key: '', value: '', description: '', placeholder: '' });
  const [editForm, setEditForm] = useState({ id: '', key: '', value: '', description: '', placeholder: '' });
  const [formError, setFormError] = useState('');
  const [editFormError, setEditFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState(null);

  useEffect(() => {
    dispatch(getSettings());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      setModalOpen && setModalOpen(false);
      setEditModalOpen && setEditModalOpen(false);
      setDeleteModalOpen && setDeleteModalOpen(false);
    };
  }, []);

  // Helper to extract a meaningful error message from backend or generic
  const getErrorMessage = (err, fallback = 'An error occurred.') => {
    if (err?.response?.data) {
      if (typeof err.response.data === 'string') return err.response.data;
      if (err.response.data.msg) return err.response.data.msg;
      if (err.response.data.message) return err.response.data.message;
      return JSON.stringify(err.response.data);
    }
    if (err?.message) return err.message;
    return fallback;
  };

  const handleAddSetting = async () => {
    setFormError("");
    if (!form.key.trim() || !form.value.trim()) {
      toast.error('Key and Value are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await dispatch(createSetting(form)).unwrap();
      setModalOpen(false);
      setForm({ key: '', value: '', description: '', placeholder: '' });
      dispatch(getSettings());
      toast.success('Setting added successfully!');
    } catch (err) {
      const msg = getErrorMessage(err, "Failed to add setting.");
      setFormError(msg);
      toast.error('Failed to add setting.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSetting = async () => {
    setEditFormError("");
    if (!editForm.key.trim() || !editForm.value.trim()) {
      toast.error('Key and Value are required.');
      return;
    }
    setEditing(true);
    try {
      const { id, ...updateData } = editForm;
      console.log('Updating setting:', { id, ...updateData });
      const res = await dispatch(updateSetting({ id, data: updateData })).unwrap();
      setEditModalOpen(false);
      setEditForm({ id: '', key: '', value: '', description: '', placeholder: '' });
      dispatch(getSettings());
      toast.success('Setting updated successfully!');
    } catch (err) {
      const msg = getErrorMessage(err, "Failed to update setting.");
      setEditFormError(msg);
      toast.error('Failed to update setting.');
    } finally {
      setEditing(false);
    }
  };

  const openEditModal = (setting) => {
    setEditForm({
      id: setting.id,
      key: setting.key,
      value: setting.value,
      description: setting.description || '',
      placeholder: setting.placeholder || ''
    });
    setEditFormError('');
    setEditModalOpen(true);
  };

  const openDeleteModal = (setting) => {
    setSettingToDelete(setting);
    setDeleteModalOpen(true);
  };

  const handleDeleteSetting = async () => {
    if (settingToDelete) {
      let id = settingToDelete.id;
      console.log('Deleting setting with id:', id, typeof id);
      if (typeof id === 'string' && id.includes('undefined')) {
        id = id.replace('undefined', '');
        console.log('Fixed id after removing undefined:', id);
      }
      if (!id || isNaN(Number(id))) {
        const msg = 'Invalid setting ID. Cannot delete.';
        return;
      }
      try {
        const res = await dispatch(deleteSetting(id)).unwrap();
        setDeleteModalOpen(false);
        setSettingToDelete(null);
        dispatch(getSettings());
        toast.success('Setting deleted successfully!');
      } catch (err) {
        const msg = getErrorMessage(err, 'Failed to delete setting.');
        toast.error('Failed to delete setting.');
      }
    }
  };

  // Add debug log before rendering
  console.log('Settings from Redux:', settings);

  // Ensure settings is always an array
  const settingsArray = Array.isArray(settings) ? settings : (settings ? Object.values(settings) : []);
  console.log('Settings Array:', settingsArray);

  const toggleAddModal = () => setModalOpen(prev => !prev);
  const toggleEditModal = () => setEditModalOpen(prev => !prev);
  const toggleDeleteModal = () => setDeleteModalOpen(prev => !prev);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Settings" pageTitle="Settings" />
        <Row className="mb-4 mt-5">
          <Col md={12}>
            <Card className="shadow-sm">
              <CardHeader className="bg-primary text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ color: '#fff', fontWeight: 600 }}>Settings Dashboard</h5>
                {/* <Button
                  className="add-button-margin"
                  style={{ backgroundColor: '#5962ef' }}
                  onClick={() => setModalOpen(true)}
                >
                  Add Setting
                </Button> */}
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center">
                    <Spinner />
                    <p className="mt-2">Loading settings...</p>
                  </div>
                ) : error ? (
                  <div className="text-danger">{error}</div>
                ) : (
                  <Table bordered responsive hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>ID</th>
                        <th>Key</th>
                        <th>Value</th>
                        <th>Description</th>
                        <th>Placeholder</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!settingsArray || settingsArray.length === 0) ? (
                        <tr>
                          <td colSpan={6} className="text-center text-muted">
                            No settings found.
                          </td>
                        </tr>
                      ) : (
                        settingsArray.map(setting => (
                          <tr key={setting.id}>
                            <td>{setting.id}</td>
                            <td>{setting.key}</td>
                            <td>{setting.value}</td>
                            <td>{setting.description}</td>
                            <td>{setting.placeholder}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <a
                                  href="#"
                                  className="text-decoration-none px-1 py-2"
                                  title="Edit"
                                  aria-label="Edit"
                                  onClick={e => { e.preventDefault(); openEditModal(setting); }}
                                >
                                  <i className="ri-edit-2-fill fs-13 text-primary p-2 rounded-circle shadow-sm bg-f1"></i>
                                </a>
                                <a
                                  href="#"
                                  className="text-decoration-none px-1 py-2"
                                  title="Delete"
                                  aria-label="Delete"
                                  onClick={e => { e.preventDefault(); openDeleteModal(setting); }}
                                >
                                  <i className="ri-delete-bin-6-fill fs-13 text-danger p-2 rounded-circle shadow-sm bg-f1"></i>
                                </a>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      
      {/* Add Setting Modal */}
      <Modal isOpen={modalOpen} toggle={toggleAddModal || (() => {})}>
        <ModalHeader toggle={toggleAddModal || (() => {})}>Add Setting</ModalHeader>
        <ModalBody>
          {formError && <div className="text-danger mb-2">{formError}</div>}
          <FormGroup>
            <Label for="settingKey">Key *</Label>
            <Input id="settingKey" value={form.key} onChange={e => setForm({ ...form, key: e.target.value })} required />
          </FormGroup>
          <FormGroup>
            <Label for="settingValue">Value *</Label>
            <Input id="settingValue" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} required />
          </FormGroup>
          <FormGroup>
            <Label for="settingDescription">Description</Label>
            <Input id="settingDescription" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label for="settingPlaceholder">Placeholder</Label>
            <Input id="settingPlaceholder" value={form.placeholder} onChange={e => setForm({ ...form, placeholder: e.target.value })} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={handleAddSetting} disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : "Add"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Setting Modal */}
      <Modal isOpen={editModalOpen} toggle={toggleEditModal || (() => {})}>
        <ModalHeader toggle={toggleEditModal || (() => {})}>Edit Setting</ModalHeader>
        <ModalBody>
          {editFormError && <div className="text-danger mb-2">{editFormError}</div>}
          <FormGroup>
            <Label for="editSettingKey">Key *</Label>
            <Input id="editSettingKey" value={editForm.key} onChange={e => setEditForm({ ...editForm, key: e.target.value })} required />
          </FormGroup>
          <FormGroup>
            <Label for="editSettingValue">Value *</Label>
            <Input id="editSettingValue" value={editForm.value} onChange={e => setEditForm({ ...editForm, value: e.target.value })} required />
          </FormGroup>
          <FormGroup>
            <Label for="editSettingDescription">Description</Label>
            <Input id="editSettingDescription" value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label for="editSettingPlaceholder">Placeholder</Label>
            <Input id="editSettingPlaceholder" value={editForm.placeholder} onChange={e => setEditForm({ ...editForm, placeholder: e.target.value })} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={handleEditSetting} disabled={editing}>
            {editing ? <Spinner size="sm" /> : "Update"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={toggleDeleteModal || (() => {})}>
        <ModalHeader toggle={toggleDeleteModal || (() => {})}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete the setting <b>{settingToDelete?.key}</b>?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button color="danger" onClick={handleDeleteSetting}>Delete</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SettingsDashboard; 